import { Suspense } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import { ADMIN_HOME } from "@/lib/loginTarget";
import * as Sentry from "@sentry/react";

import { CookieConsentBanner } from "./components/candidate/CookieConsentBanner";
import { CandidateRouteErrorFallback } from "./components/ErrorBoundaryFallbacks";
import { RequireAuth } from "./components/RequireAuth";
import { RequireCapability } from "./components/RequireCapability";
import { RequirePlan } from "./components/RequirePlan";
import { RequirePlatformOperator, RequireSuper } from "./components/RequireSuper";
import { lazyWithRetry } from "./lib/lazyWithRetry";
import { isOpsPortal } from "./lib/portal";
// Candidate-flow routes stay EAGER — they're the critical path candidates load
// first, so they belong in the entry bundle (not behind a Suspense boundary).
import Done from "./routes/Done";
import Gate from "./routes/Gate";
import IdentityDone from "./routes/IdentityDone";
import Paused from "./routes/Paused";
import Practice from "./routes/Practice";
import Question from "./routes/Question";
import Review from "./routes/Review";
import SectionReview from "./routes/SectionReview";

// Everything below is code-split with lazyWithRetry (a stale-chunk-safe
// React.lazy): candidates never execute admin/auth/legal/backoffice code, and
// recruiters never load the candidate flow. Keeping these out of the entry
// chunk is the bulk of the candidate first-paint win (perf P3). lazyWithRetry
// reloads once on a post-deploy stale-chunk import failure instead of crashing.

// Admin dashboard + management pages (gated behind RequireAuth).
const AdminCreate = lazyWithRetry(() => import("./routes/admin/Create"));
const AdminCreateAi = lazyWithRetry(() => import("./routes/admin/CreateAi"));
const AdminCreateSoftSkills = lazyWithRetry(
  () => import("./routes/admin/CreateSoftSkills"),
);
const AdminDashboard = lazyWithRetry(() => import("./routes/admin/Dashboard"));
const AdminDetail = lazyWithRetry(() => import("./routes/admin/Detail"));
const AdminHomePage = lazyWithRetry(() => import("./routes/admin/Home"));
const AdminList = lazyWithRetry(() => import("./routes/admin/List"));
const CandidatesPage = lazyWithRetry(() => import("./routes/admin/Candidates"));
const AdminTagsPage = lazyWithRetry(() => import("./routes/admin/Tags"));
const AdminTeamPage = lazyWithRetry(() => import("./routes/admin/Team"));
const BillingPage = lazyWithRetry(() => import("./routes/admin/billing/BillingPage"));
const CompanySettingsPage = lazyWithRetry(() => import("./routes/admin/CompanySettings"));
const JobDetailPage = lazyWithRetry(() => import("./routes/admin/JobDetail"));
const JobsListPage = lazyWithRetry(() => import("./routes/admin/Jobs"));
const ProfilePage = lazyWithRetry(() => import("./routes/admin/Profile"));
const EditTestPage = lazyWithRetry(() => import("./routes/admin/EditTest"));
const PreviewTestPage = lazyWithRetry(() => import("./routes/admin/PreviewTest"));
const QuestionEditPage = lazyWithRetry(() => import("./routes/admin/QuestionEdit"));
const QuestionsLibraryPage = lazyWithRetry(() => import("./routes/admin/Questions"));
const TemplatesPage = lazyWithRetry(() => import("./routes/admin/Templates"));
const TemplatePreviewPage = lazyWithRetry(() => import("./routes/admin/TemplatePreview"));
const SupportPage = lazyWithRetry(() => import("./routes/admin/Support"));
const FaqPage = lazyWithRetry(() => import("./routes/admin/Faq"));
const TraffitPage = lazyWithRetry(
  () => import("./routes/admin/integrations/TraffitPage"),
);

// recharts stays isolated in Statistics' own chunk — keep Statistics the SOLE
// importer of recharts (memory project_recharts_vite_pin).
const StatisticsPage = lazyWithRetry(() => import("./routes/admin/Statistics"));

// Public auth pages.
const AcceptInvitePage = lazyWithRetry(() => import("./routes/auth/AcceptInvite"));
const ForgotPasswordPage = lazyWithRetry(() => import("./routes/auth/ForgotPassword"));
const LoginPage = lazyWithRetry(() => import("./routes/auth/Login"));
// FEAT-47: the AI-assistant consent page (behind sign-in) and the page an
// untrusted authorization request ends on (public). `/connect/` is proxied
// to nothing — it is ours; `/mcp`, `/oauth/` and `/.well-known/` are the
// backend's and no SPA route may start with them.
const ConnectAuthorizePage = lazyWithRetry(() => import("./routes/connect/Authorize"));
const ConnectErrorPage = lazyWithRetry(() => import("./routes/connect/Error"));
const ResetPasswordPage = lazyWithRetry(() => import("./routes/auth/ResetPassword"));
const SignupPage = lazyWithRetry(() => import("./routes/auth/Signup"));
const SignInLinkPage = lazyWithRetry(() => import("./routes/auth/SignInLink"));
const JoinRequestConfirmPage = lazyWithRetry(() => import("./routes/auth/JoinRequestConfirm"));
const VerifyEmailPage = lazyWithRetry(() => import("./routes/auth/VerifyEmail"));

// Legal / compliance pages — large verbatim policy text, no reason to ship in
// the candidate entry bundle (they open in a new tab anyway).
const CandidatePrivacyNoticePage = lazyWithRetry(
  () => import("./routes/legal/CandidatePrivacyNoticePage"),
);
const CookiesPage = lazyWithRetry(() => import("./routes/legal/CookiesPage"));
const PrivacyPage = lazyWithRetry(() => import("./routes/legal/PrivacyPage"));
const SubProcessorsPage = lazyWithRetry(
  () => import("./routes/legal/SubProcessorsPage"),
);
const RefundPolicyPage = lazyWithRetry(
  () => import("./routes/legal/RefundPolicyPage"),
);
const TermsPage = lazyWithRetry(() => import("./routes/legal/TermsPage"));

// Paddle's default payment link (FEAT-2) — public like the legal pages, because
// Paddle's own emails open it signed-out with `?_ptxn=` in the URL.
const PayPage = lazyWithRetry(() => import("./routes/pay/PayPage"));

// Public developer API reference (OPS-5) — same public, no-auth group as the
// legal pages; documents the /api/v1 REST surface + webhooks.
const DevelopersPage = lazyWithRetry(
  () => import("./routes/developers/DevelopersPage"),
);

// The CANDIDATE Help Center (FEAT-22) — public, no auth: the invitation and
// reminder emails link here (`{{help_link}}`), and a candidate has no account.
const HelpPage = lazyWithRetry(() => import("./routes/help/HelpPage"));

// Creator-only Back office (MVP 4.0) — only platform operators load this chunk.
const BackofficeLayout = lazyWithRetry(() => import("./routes/backoffice/BackofficeLayout"));
const BackofficeHome = lazyWithRetry(() => import("./routes/backoffice/BackofficeHome"));
const BackofficeOverview = lazyWithRetry(() => import("./routes/backoffice/Overview"));
const BackofficeReports = lazyWithRetry(() => import("./routes/backoffice/ProblemReports"));
const BackofficeCompanies = lazyWithRetry(() => import("./routes/backoffice/Companies"));
const BackofficeCompanyDetail = lazyWithRetry(
  () => import("./routes/backoffice/CompanyDetail"),
);
const BackofficeRecruiters = lazyWithRetry(() => import("./routes/backoffice/Recruiters"));
const BackofficeAntiCheat = lazyWithRetry(() => import("./routes/backoffice/AntiCheat"));
const BackofficeRecovery = lazyWithRetry(() => import("./routes/backoffice/Recovery"));
const BackofficeOperators = lazyWithRetry(() => import("./routes/backoffice/Operators"));
const BackofficeLibraryQuestions = lazyWithRetry(() => import("./routes/backoffice/LibraryQuestions"));
const BackofficeLibraryTemplates = lazyWithRetry(() => import("./routes/backoffice/LibraryTemplates"));
const BackofficeBilling = lazyWithRetry(() => import("./routes/backoffice/Billing"));
const BackofficeAiUsage = lazyWithRetry(() => import("./routes/backoffice/AiUsage"));
const BackofficeCodeRuns = lazyWithRetry(() => import("./routes/backoffice/CodeRunsUsage"));
const BackofficeIntegrations = lazyWithRetry(() => import("./routes/backoffice/Integrations"));

// Wraps the candidate-flow routes in a Sentry.ErrorBoundary keyed by pathname,
// so a crash on /t/x/question/1 doesn't keep the fallback rendered after the
// candidate clicks Next. Softer copy than the admin boundary because the
// candidate isn't a power user — see ErrorBoundaryFallbacks.tsx.
//
// Admin routes get their own boundary inside AdminLayout (Layer 2) — that one
// preserves the sidebar chrome on crash. This boundary is the equivalent for
// the candidate flow where there's no shared chrome.
//
// The whole candidate flow is locked to a single 100%-viewport window
// (`h-screen`) laid out as a flex column so the body never scrolls — the
// candidate sees a clean, fixed test surface. The routed pages live in an inner
// `flex-1 min-h-0 overflow-y-auto` scroll region: on a normal/fullscreen viewport
// nothing overflows so no scrollbar appears, but on a short viewport a tall card
// can still be reached by scrolling that region (centered screens use
// `min-h-full`, which grows past the region rather than clipping the top — the
// flexbox items-center + overflow trap). The Privacy/Terms/Cookies footer links
// were removed for a distraction-free surface; the cookie-consent banner stays
// (GDPR) DOCKED BELOW the scroll region as an in-flow, full-width bottom bar. It
// reserves its own height (pushing page content — including the sticky Review /
// SectionReview footers — up) instead of floating, so it can never overlay a
// candidate action button (fix P8). CookieConsentBanner sits OUTSIDE the keyed
// boundary so its `animate-cookie-banner` entrance doesn't re-fire on every route
// transition.
// `translate="no"` + `.notranslate` (both, because Chrome honours the attribute
// and older Google Translate paths key off the class) are LOAD-BEARING, not
// cosmetic — FRONTEND-REACT-8, 2026-08-25. Chrome auto-translated the English
// assessment for a pl-locale candidate; Translate rewrites text nodes into
// `<font>` wrappers, so when React later unmounted the Gate's card subtree,
// `commitDeletionEffectsOnFiber` -> `hostParent.removeChild(stateNode)` hit a
// node Translate had re-parented and threw NotFoundError. The candidate had
// already POSTed /start, so the boundary's "Reload page" then dead-ended on a
// consumed invitation (410). Blocking translation removes the DOM mutation at
// the root. It also protects the instrument itself: Translate mangles the Python
// snippets inside code questions, which changes what the test measures.
//
// Scoped to the candidate flow ONLY — /admin stays translatable for
// Polish-speaking recruiters. Safe as a subtree block because the candidate flow
// has no portals: the codebase's only createPortal is TourOverlay (admin-only).
//
// night-p5: the `:token` segment of a candidate URL (`/t/<token>/...`), or
// null off the candidate flow. A malformed percent-escape yields null rather
// than a throw — this runs inside the crash fallback.
function candidateTokenFromPath(pathname: string): string | null {
  const m = /^\/t\/([^/]+)/.exec(pathname);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return null;
  }
}

function CandidateRouteLayout() {
  const { pathname } = useLocation();
  return (
    <div className="notranslate flex h-screen flex-col" translate="no">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Sentry.ErrorBoundary
          key={pathname}
          // night-p5: the fallback keeps "Report a problem", which needs the
          // link's token. Read off the pathname rather than useParams — the
          // fallback renders in place of the crashed child route, and this
          // layout is a pathless parent.
          fallback={<CandidateRouteErrorFallback token={candidateTokenFromPath(pathname)} />}
          showDialog={false}
        >
          <Outlet />
        </Sentry.ErrorBoundary>
      </div>
      <CookieConsentBanner />
    </div>
  );
}

// Shared full-viewport fallback for lazy route chunks, used as a pathless layout
// route around each lazy group (admin / auth+legal / ops auth). The fallback is
// BARE (just the background) because each admin page renders its own AdminLayout
// sidebar, and each auth/legal page its own shell, INSIDE its lazy chunk — so
// nothing chrome-like can paint until the chunk resolves. Matches RequireAuth's
// own loading state (RequireAuth.tsx).
function SuspendedOutlet() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <Outlet />
    </Suspense>
  );
}

export default function App() {
  // The /backoffice subtree is shared by both portals: it stays reachable on the
  // apex during the transition, and it IS the whole operations portal on ops.*.
  // Defining it once keeps the two trees in sync. (React Router accepts a <Route>
  // held in a variable as a direct <Routes> child.)
  const backofficeRoutes = (
    <Route element={<RequirePlatformOperator />}>
      <Route
        path="/backoffice"
        element={
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <BackofficeLayout />
          </Suspense>
        }
      >
        {/* Index dispatches by role: superuser → Overview, support → Recovery. */}
        <Route index element={<BackofficeHome />} />
        {/* Operator-level (support + superuser). */}
        <Route path="recovery" element={<BackofficeRecovery />} />
        <Route path="reports" element={<BackofficeReports />} />
        {/* Superuser-only tabs. */}
        <Route element={<RequireSuper />}>
          <Route path="overview" element={<BackofficeOverview />} />
          <Route path="library-questions" element={<BackofficeLibraryQuestions />} />
          <Route path="library-templates" element={<BackofficeLibraryTemplates />} />
          <Route path="companies" element={<BackofficeCompanies />} />
          {/* EP-1 — deep-linkable, so an operator email or a shared link
              can point straight at one workspace. */}
          <Route
            path="companies/:companyId"
            element={<BackofficeCompanyDetail />}
          />
          <Route path="billing" element={<BackofficeBilling />} />
          <Route path="ai-usage" element={<BackofficeAiUsage />} />
          {/* FEAT-12 Build 6: the code runner's execution meter. */}
          <Route path="code-runs" element={<BackofficeCodeRuns />} />
          {/* Traffit usage folded into the Integrations tab as a sub-view;
              keep the old path as a redirect so bookmarks don't 404. */}
          <Route
            path="ats-usage"
            element={
              <Navigate to="/backoffice/integrations?view=traffit" replace />
            }
          />
          <Route path="integrations" element={<BackofficeIntegrations />} />
          <Route path="recruiters" element={<BackofficeRecruiters />} />
          <Route path="anticheat" element={<BackofficeAntiCheat />} />
          <Route path="operators" element={<BackofficeOperators />} />
        </Route>
      </Route>
    </Route>
  );

  // Operations portal (ops.interview-tool.click) — serves ONLY the back office
  // plus the auth pages an operator needs. No candidate flow, no recruiter
  // dashboard; the catch-all lands on /backoffice (NOT /admin, which has no
  // route here — that would dead-end). The shared session cookie means an
  // operator already signed in on the apex is recognized here without a second
  // login.
  if (isOpsPortal) {
    return (
      <Routes>
        <Route element={<SuspendedOutlet />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
        </Route>
        {backofficeRoutes}
        <Route path="*" element={<Navigate to="/backoffice" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<CandidateRouteLayout />}>
        <Route path="/t/:token" element={<Gate />} />
        <Route path="/t/:token/practice" element={<Practice />} />
        <Route path="/t/:token/question/:n" element={<Question />} />
        <Route path="/t/:token/review" element={<Review />} />
        <Route path="/t/:token/section-review" element={<SectionReview />} />
        {/* EF-2: the paused screen. Nothing anti-cheat mounts here. */}
        <Route path="/t/:token/paused" element={<Paused />} />
        <Route path="/t/:token/done" element={<Done />} />
        {/* EF-3: where the identity vendor returns the candidate (a new tab).
            Static, no token in the path — and it MUST be a real route: the
            catch-all below would send a candidate to the recruiter login. */}
        <Route path="/identity/done" element={<IdentityDone />} />
      </Route>
      {/* Auth + legal pages — all lazy-loaded, so one shared Suspense boundary
          (SuspendedOutlet) wraps the whole group. Outside AdminLayout +
          CandidateRouteLayout so they render without sidebar/candidate chrome;
          literal segments don't collide with /t/:token or /admin/:token per
          project_router_empty_path_collision.md. */}
      <Route element={<SuspendedOutlet />}>
        {/* Auth routes — MVP 2.0 #2. Don't redirect to /login themselves. */}
        <Route path="/login" element={<LoginPage />} />
        {/* Open self-serve signup (MVP 3.0 team model): creates a new company
            workspace + its owner account. */}
        <Route path="/signup" element={<SignupPage />} />
        {/* Team-invite acceptance — an invited recruiter sets their own password
            and joins the company. Public (no session), like the other
            token-based auth pages. */}
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        {/* FEAT-48: the emailed sign-in link — verifies the address and signs
            the person in. The token is a PATH segment (redacted by
            lib/observability.ts like /t/<token>); the route is literal under
            /auth/, which collides with nothing. */}
        <Route path="/auth/link/:token" element={<SignInLinkPage />} />
        <Route path="/auth/join/:token" element={<JoinRequestConfirmPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Legal / compliance pages — MVP 2.0 #9. */}
        <Route path="/privacy" element={<PrivacyPage />} />
        {/* Candidate-facing privacy notice — the pre-test consent checkbox in
            Gate.tsx links here (opens in a new tab so the form isn't lost). */}
        <Route path="/privacy-notice" element={<CandidatePrivacyNoticePage />} />
        <Route path="/terms" element={<TermsPage />} />
        {/* Paddle's domain review needs a refund policy the site links to. */}
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/sub-processors" element={<SubProcessorsPage />} />
        {/* Public developer API reference (OPS-5) — the URL Zapier's App
            Directory submission points at; no auth, like the legal pages. */}
        <Route path="/developers" element={<DevelopersPage />} />
        {/* The candidate Help Center (FEAT-22). MUST be a real route: the
            catch-all below would send a candidate to the recruiter login. */}
        <Route path="/help" element={<HelpPage />} />
        <Route path="/pay" element={<PayPage />} />
        {/* FEAT-47: where an authorization request nobody can vouch for ends
            (an unknown app, an unregistered return address) — public. */}
        <Route path="/connect/error" element={<ConnectErrorPage />} />
      </Route>
      {/* Session gate for every admin route. Prevents the per-page useQuery
          calls (e.g., Dashboard's fetchWorkspaceTests)
          from firing pre-redirect when an unauthenticated user navigates to
          /admin/* — those queries 401 cleanly but log "Failed to load
          resource" in the console. RequireAuth resolves the session first,
          then mounts the page only if logged in. */}
      <Route element={<RequireAuth />}>
        {/* FEAT-47: the AI-assistant consent page — signed in (RequireAuth
            keeps its `?rid=` through the sign-in), outside the admin chrome
            and outside the plan wall: a walled workspace must still SEE the
            refusal the page explains. */}
        <Route element={<SuspendedOutlet />}>
          <Route path="/connect/authorize" element={<ConnectAuthorizePage />} />
        </Route>
        {/* One Suspense boundary for the whole admin subtree — every admin page
            below is lazy-loaded. Bare fallback: each page renders its own
            AdminLayout sidebar inside its chunk, so nothing can paint until
            the chunk resolves. FEAT-34: that boundary is `RequirePlan`, which
            also swaps every page but Billing for the plan wall while the
            workspace must pick a plan — so a walled page is never mounted. */}
        <Route element={<RequirePlan />}>
          <Route path="/admin" element={<AdminDashboard />} />
          {/* FEAT-28: the AUTHORING pages — everything that builds or changes
              a test, a question, a tag or a template, plus Billing. A role
              without `workspace.write` (a hiring manager) gets the notice page
              on a typed URL; the nav never offers these to them. A pathless
              layout route, so it changes nothing about how these paths rank
              against /admin/:token below. */}
          <Route element={<RequireCapability capability="workspace.write" />}>
            {/* FEAT-39: creating a test is Emilia's 'Creating and duplicating
                the tests' — a second, narrower door inside the authoring group. */}
            <Route element={<RequireCapability capability="tests.create" />}>
              <Route path="/admin/new" element={<AdminCreate />} />
              {/* AI test creation from a job description (MVP 4.0 item 4). Two-segment
                  literal — unambiguous with the single-segment /admin/:token below,
                  but kept in this literal group for consistency. */}
              <Route path="/admin/new/ai" element={<AdminCreateAi />} />
              <Route path="/admin/new/soft-skills" element={<AdminCreateSoftSkills />} />
            </Route>
            <Route path="/admin/questions" element={<QuestionsLibraryPage />} />
            {/* Distinct `key` per route forces a fresh mount when navigating between
                create ↔ edit. They share `QuestionEditPage`, so React would otherwise
                reuse the instance — leaving stale `seedFromQuestionId` / `draft` /
                `attachTokens` state from the prior mount. */}
            <Route
              path="/admin/questions/new"
              element={<QuestionEditPage key="create" mode="create" />}
            />
            <Route
              path="/admin/questions/:id/edit"
              element={<QuestionEditPage key="edit" mode="edit" />}
            />
            {/* Literal-segment routes MUST precede /admin/:token below — see memory
                project_router_empty_path_collision.md. Without this, /admin/tags
                and /admin/jobs would resolve as token="tags"/"jobs" and hit
                AdminList. */}
            <Route path="/admin/tags" element={<AdminTagsPage />} />
            {/* Ready-made test templates (MVP 4.0 item 2). Literal segment —
                same precede-/admin/:token rule as the routes around it. */}
            <Route path="/admin/templates" element={<TemplatesPage />} />
            <Route
              path="/admin/templates/:id/preview"
              element={<TemplatePreviewPage />}
            />
            <Route path="/admin/:token/edit" element={<EditTestPage />} />
          </Route>
          {/* FEAT-39: Billing is Emilia's 'Billing' box, not authoring — the
              nav offers it on `billing.view`, so the page asks the same
              (pre-ship 2026-10-18 R10: it sat behind workspace.write, so a
              hiring manager given the box landed on RoleBlocked). */}
          <Route element={<RequireCapability capability="billing.view" />}>
            <Route path="/admin/billing" element={<BillingPage />} />
          </Route>
          <Route path="/admin/jobs" element={<JobsListPage />} />
          <Route path="/admin/jobs/:job_id" element={<JobDetailPage />} />
          <Route path="/admin/home" element={<AdminHomePage />} />
          <Route path="/admin/team" element={<AdminTeamPage />} />
          <Route path="/admin/settings" element={<CompanySettingsPage />} />
          {/* Dedicated Traffit ATS page (MVP 4.0 item 7). Two-segment literal —
              must precede /admin/:token below (same rule as its siblings). The
              page self-guards on ats_enabled (resale gate); the sidebar item is
              hidden for non-Traffit tenants. */}
          <Route
            path="/admin/integrations/traffit"
            element={<TraffitPage />}
          />
          <Route path="/admin/me" element={<ProfilePage />} />
          <Route path="/admin/support" element={<SupportPage />} />
          {/* Static Help Center / FAQ (MVP 4.0 item 8). Literal segment — must
              precede /admin/:token below, same rule as its siblings. */}
          <Route path="/admin/faq" element={<FaqPage />} />
          {/* recharts code-split lives in StatisticsPage's own chunk; the parent
              SuspendedOutlet provides its loading fallback (keep Statistics the
              sole importer of recharts, memory project_recharts_vite_pin). */}
          <Route path="/admin/statistics" element={<StatisticsPage />} />
          {/* Cross-test candidate directory (CP-23). Literal segment — must
              precede /admin/:token below, same rule as its siblings; without
              it the page renders "Invalid admin link" with no error. */}
          <Route path="/admin/candidates" element={<CandidatesPage />} />
          {/* /admin/login + /admin/tests are not routes — but a logged-in user
              typing them (or sharing a stale URL) would otherwise hit the
              /admin/:token catch-all below and see "Invalid admin link".
              Redirect to /admin/home (for login) or /admin (for tests — the
              tests grid lives at the bare /admin path); RequireAuth above
              handles the unauthed → /login bounce. Same shape used for any
              other reserved literal that's not otherwise a route. /admin/tests
              added 2026-05-27 (pre-ship-audit QA-008). */}
          <Route
            path="/admin/login"
            element={<Navigate to={ADMIN_HOME} replace />}
          />
          <Route
            path="/admin/tests"
            element={<Navigate to="/admin" replace />}
          />
          {/* The back office lives at the top-level /backoffice, but /admin/backoffice
              is a natural guess — without this it falls through to /admin/:token
              below and renders "Invalid admin link". Literal segment, so it must
              precede /admin/:token. */}
          <Route
            path="/admin/backoffice"
            element={<Navigate to="/backoffice" replace />}
          />
          <Route path="/admin/:token" element={<AdminList />} />
          {/* /admin/:token/edit is in the authoring group above (FEAT-28). */}
          {/* Preview as candidate (MVP 4.0 item 6): authenticated admin route,
              so it never touches the anonymous /t/:token candidate machinery
              (no attempt/events/consent/anti-cheat). */}
          <Route path="/admin/:token/preview" element={<PreviewTestPage />} />
          <Route path="/admin/:token/attempts/:id" element={<AdminDetail />} />
        </Route>
      </Route>
      {/* The bare domain. Without this it fell through the `*` catch-all below
          to /admin, which meant RequireAuth bounced an unauthenticated visitor
          to /login carrying `from: "/admin"` — and Login honours a `/admin*`
          `from`, so typing the domain landed everyone on the tests dashboard.
          For a brand-new recruiter that silently skipped the whole first-run
          tour, which only ever mounts on /admin/home (see lib/loginTarget.ts).
          Home is also simply the better landing: it's the attention-first page. */}
      <Route path="/" element={<Navigate to={ADMIN_HOME} replace />} />
      {/* Creator-only Back office (MVP 4.0). Separate top-level tree, gated on
          platform-operator access (support OR superuser); the analytics/PII tabs
          + operator management nest a stricter <RequireSuper>. The /backoffice
          prefix is a distinct literal, so it never collides with /admin/:token
          or /t/:token. Kept reachable on the apex during the ops-portal
          transition; the same subtree is the whole operations portal on ops.*. */}
      {backofficeRoutes}
      {/* Unknown paths land where the bare domain does — the same `from:
          "/admin"` bounce the `/` route above exists to avoid (SIMP-13). */}
      <Route path="*" element={<Navigate to={ADMIN_HOME} replace />} />
    </Routes>
  );
}
